import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { selectVariant, getSessionId } from '@/utils/abTesting';
import { useAuth } from './AuthContext';

interface ABTestContextType {
  getVariant: (testName: string) => string;
  trackEvent: (testName: string, eventType: string, eventData?: any) => Promise<void>;
  trackConversion: (testName: string) => Promise<void>;
}

const ABTestContext = createContext<ABTestContextType | null>(null);

export function useABTest(testName: string) {
  const context = useContext(ABTestContext);
  if (!context) {
    throw new Error('useABTest must be used within ABTestProvider');
  }

  const variant = context.getVariant(testName);

  const track = (eventType: string, eventData?: any) => {
    return context.trackEvent(testName, eventType, eventData);
  };

  const trackConversion = () => {
    return context.trackConversion(testName);
  };

  return { variant, track, trackConversion };
}

export function ABTestProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState<Map<string, string>>(new Map());
  const [tests, setTests] = useState<Map<string, any>>(new Map());

  useEffect(() => {
    loadActiveTests();
  }, []);

  const loadActiveTests = async () => {
    const { data } = await supabase
      .from('ab_tests')
      .select('*')
      .eq('status', 'active');

    if (data) {
      const testMap = new Map(data.map((test) => [test.test_name, test]));
      setTests(testMap);
    }
  };

  const getVariant = (testName: string): string => {
    // Check if already assigned
    if (assignments.has(testName)) {
      return assignments.get(testName)!;
    }

    // Get test configuration
    const test = tests.get(testName);
    if (!test) {
      return 'control'; // Default fallback
    }

    // Select variant
    const variant = selectVariant(test.variants, user?.id);

    // Save assignment
    saveAssignment(testName, test.id, variant);

    return variant;
  };

  const saveAssignment = async (testName: string, testId: string, variant: string) => {
    setAssignments((prev) => new Map(prev).set(testName, variant));

    await supabase.from('ab_test_assignments' as any).insert({
      user_id: user?.id,
      session_id: getSessionId(),
      test_id: testId,
      variant_name: variant,
    });
  };

  const trackEvent = async (testName: string, eventType: string, eventData?: any) => {
    const test = tests.get(testName);
    const variant = assignments.get(testName);

    if (!test || !variant) return;

    await supabase.from('ab_test_events' as any).insert({
      test_id: test.id,
      user_id: user?.id,
      session_id: getSessionId(),
      variant_name: variant,
      event_type: eventType,
      event_data: eventData || {},
    });
  };

  const trackConversion = async (testName: string) => {
    const test = tests.get(testName);
    const variant = assignments.get(testName);

    if (!test || !variant) return;

    // Update assignment
    await supabase
      .from('ab_test_assignments' as any)
      .update({ converted: true, converted_at: new Date().toISOString() })
      .eq('test_id', test.id)
      .eq('session_id', getSessionId());

    // Track conversion event
    await trackEvent(testName, 'conversion');
  };

  return (
    <ABTestContext.Provider value={{ getVariant, trackEvent, trackConversion }}>
      {children}
    </ABTestContext.Provider>
  );
}
